import type { QueryClient } from '@tanstack/react-query';
import type { TKey } from '@/i18n/types';
import { createRouter } from '@tanstack/react-router';

import { LoadingState } from '@/components/state/LoadingState';
import { NotFound, RouteError } from '@/components/state/RouteError';

import { routeTree } from './route-tree';

/**
 * Router configuration is fixed by contract (§14.2): Query owns freshness, not the router
 * (`defaultPreloadStaleTime: 0`), and a load faster than 300 ms never flashes a skeleton —
 * once shown, it stays 400 ms so the UI does not strobe.
 */
export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadDelay: 50,
    defaultPreloadStaleTime: 0,
    defaultPendingMs: 300,
    defaultPendingMinMs: 400,
    scrollRestoration: true,
    defaultPendingComponent: LoadingState,
    defaultErrorComponent: ({ error }) => <RouteError error={error} />,
    defaultNotFoundComponent: NotFound,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
  interface StaticDataRouteOption {
    /** Which menu entry this route belongs to. The topbar takes the name from there. */
    navId?: string;
    /**
     * The name for a route that is deliberately NOT a menu entry — the account behind the avatar,
     * the settings layout, a festival page whose `navId` names a section rather than an entry.
     *
     * Added 28 August 2026, when the topbar was found rendering "Innstillinger" on `/profile` and
     * on `/festivals/:id`: it fell back to Settings' name whenever `navEntryById` came up empty,
     * which was true of exactly one page when that line was written. A page now says its own name
     * or says nothing.
     */
    titleKey?: TKey;
  }
}
